import type { UniqueIdentifier } from '@dnd-kit/core';
import { useEffect, useMemo, useState } from 'react';

interface BaseItem {
  id: UniqueIdentifier;
}

/**
 * Bridges the one-frame "snap-back" flash that appears when a sortable list
 * is backed by server state and updated optimistically.
 *
 * The flash happens because dnd-kit's `onDragEnd` and TanStack Query's
 * optimistic cache write both run synchronously, but TanStack's
 * `notifyManager` defers the cache-change notification to a macrotask. For
 * one render the source `items` prop still holds the pre-drop order, so
 * the dragged item visibly snaps back before landing.
 *
 * Usage: call `commit(newItems)` from `onDragEnd` to record the new id
 * sequence, then render from `displayItems` instead of the raw `items`
 * prop. `displayItems` reorders the live `items` by the pending sequence,
 * so unrelated edits to individual items (e.g. an in-flight title save)
 * still flow through.
 *
 * The pending order auto-clears when:
 * - The `items` prop catches up — its order matches the pending sequence,
 *   typically because the optimistic cache update has propagated.
 * - The id set diverges — items are added, removed, or moved across
 *   containers, so the snapshot is no longer meaningful.
 *
 * Because the clear is driven by the `items` prop rather than mutation
 * callbacks or React render timing, the source `useQuery` can live
 * anywhere in the tree — no colocation or `onMutate` wiring required.
 *
 * Reference: https://github.com/clauderic/dnd-kit/discussions/1522.
 */
const useOptimisticReorder = <T extends BaseItem>(items: T[]) => {
  const [pendingOrder, setPendingOrder] = useState<UniqueIdentifier[] | null>(
    null
  );

  const displayItems = useMemo(() => {
    if (!pendingOrder || pendingOrder.length !== items.length) return items;
    const byId = new Map(items.map((i) => [i.id, i]));
    if (!pendingOrder.every((id) => byId.has(id))) return items;
    return pendingOrder.map((id) => byId.get(id)!);
  }, [pendingOrder, items]);

  useEffect(() => {
    if (!pendingOrder) return;
    if (pendingOrder.length !== items.length) {
      setPendingOrder(null);
      return;
    }
    const sameOrder = pendingOrder.every((id, i) => id === items[i].id);
    const sameSet =
      sameOrder || pendingOrder.every((id) => items.some((i) => i.id === id));
    if (sameOrder || !sameSet) setPendingOrder(null);
  }, [items, pendingOrder]);

  const commit = (newItems: T[]) => setPendingOrder(newItems.map((i) => i.id));

  return { displayItems, commit };
};

export default useOptimisticReorder;
